import { useMemo, useState } from "react";
import { ApiError } from "@/api/client";
import {
  useApplyWfh,
  useCancelWfh,
  useHolidays,
  useMyWfh,
  useWfhEligibility,
  type WfhInput,
} from "@/api/queries";
import { StatTile } from "@/components/charts";
import { Badge, StatusBadge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
} from "@/components/ui/dialog";
import { Field, Input, Textarea } from "@/components/ui/field";
import { Icon } from "@/components/ui/icon";
import { Panel, PanelHeader } from "@/components/ui/panel";
import { EmptyState, SkeletonTable } from "@/components/ui/states";
import { TBody, TD, TDPrimary, TH, THead, TR, TableWrap } from "@/components/ui/table";
import { toast } from "@/components/ui/toast";
import {
  calendarTodayStr,
  countWorkingDays,
  earliestFilingDate,
  eachDateInRange,
  holidayMap,
  isRestDay,
  todayStr,
  weekBounds,
} from "@/lib/calendar";
import { formatDate, formatTimestampDate } from "@/lib/format";

/**
 * Your own work-from-home requests: ask for days, see what was decided, cancel
 * one nobody has decided yet.
 *
 * Rendered as a tab on the leave page rather than a page of its own, and the
 * distinction it has to keep making is exactly why it is a separate panel and a
 * separate table: this is a day WORKED. It costs no entitlement, appears on no
 * balance, and the copy here never calls it leave.
 */
export function MyWfhPanel() {
  const requests = useMyWfh();
  const eligibility = useWfhEligibility();
  const holidays = useHolidays();
  const cancel = useCancelWfh();
  const [applying, setApplying] = useState(false);

  const rows = requests.data ?? [];
  const pending = rows.filter((request) => request.status === "pending");
  const eligible = eligibility.data?.eligible ?? false;
  const fixedHolidaySet = useMemo(() => new Set(holidayMap(holidays.data).keys()), [holidays.data]);

  /*
    What this week actually came to, from approved requests only.

    Counted as DISTINCT working days, which is how the backend counts the same
    thing (utils/wfhCalendar.js dedupes to one row per user-day before counting).
    This used to sum each request's overlap separately, so two approved requests
    covering the same day counted it twice and the employee's tile could read
    higher than the manager's chart for the same person and week — while the
    comment above it claimed the two could not disagree. Overlapping requests are
    normally blocked at apply time, so this only ever bit records created before
    that rule existed; a figure that agrees by construction is still worth more
    than one that agrees by luck.
  */
  const daysThisWeek = useMemo(() => {
    const week = weekBounds(todayStr());
    const days = new Set<string>();
    for (const request of rows) {
      if (request.status !== "approved") continue;
      if (request.start_date > week.end || request.end_date < week.start) continue;
      const from = request.start_date > week.start ? request.start_date : week.start;
      const to = request.end_date < week.end ? request.end_date : week.end;
      if (from > to) continue;
      for (const date of eachDateInRange(from, to)) {
        if (!isRestDay(date, fixedHolidaySet)) days.add(date);
      }
    }
    return days.size;
  }, [rows, fixedHolidaySet]);

  const approvedTotal = rows
    .filter((request) => request.status === "approved")
    .reduce((sum, request) => sum + request.days, 0);

  return (
    <div className="flex flex-col gap-6">
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        <StatTile
          label="Waiting on a decision"
          value={pending.length}
          icon="warning-circle"
          loading={requests.isLoading}
          meta={pending.length === 0 ? "Nothing outstanding" : "With your approver"}
        />
        <StatTile
          label="Days from home this week"
          value={daysThisWeek}
          icon="wfh"
          loading={requests.isLoading}
          meta="Approved, Monday to Sunday"
        />
        <StatTile
          label="Days approved in total"
          value={approvedTotal}
          icon="calendar-check"
          loading={requests.isLoading}
          meta="Across every request on record"
        />
      </div>

      {!eligibility.isLoading && !eligible && (
        /* Saying why the button is missing, rather than simply not drawing one.
           A remote account has nothing to request; anything else here is a real
           refusal the person needs to be able to read. */
        <Panel className="border-line bg-surface-sunken/60">
          <div className="flex items-start gap-3 p-4">
            <Icon name="info" size={20} className="mt-0.5 shrink-0 text-muted" />
            <div className="min-w-0">
              <p className="text-sm font-medium text-ink">
                You cannot raise a work-from-home request
              </p>
              <p className="mt-1 max-w-[70ch] text-sm leading-relaxed text-ink-soft">
                {eligibility.data?.reason ??
                  "Your account is not set up to request work from home."}
              </p>
            </div>
          </div>
        </Panel>
      )}

      <Panel>
        <PanelHeader
          title="Your work-from-home requests"
          note="Newest first · a work-from-home day is a day worked, so it costs no leave"
          actions={
            eligible ? (
              <Button variant="primary" size="sm" iconLeft="plus" onClick={() => setApplying(true)}>
                Request work from home
              </Button>
            ) : undefined
          }
        />
        {requests.isLoading ? (
          <SkeletonTable rows={4} cols={5} />
        ) : rows.length === 0 ? (
          <EmptyState
            icon="wfh"
            title="You have not asked to work from home yet"
            body={
              eligible
                ? "Pick the days and say why. It goes to your manager for a decision."
                : "Nothing to show for this account."
            }
            action={
              eligible ? (
                <Button variant="primary" iconLeft="plus" onClick={() => setApplying(true)}>
                  Request work from home
                </Button>
              ) : undefined
            }
          />
        ) : (
          <TableWrap>
            <THead>
              <TH>Dates</TH>
              <TH numeric>Days</TH>
              <TH>Reason</TH>
              <TH>Status</TH>
              <TH>
                <span className="sr-only">Actions</span>
              </TH>
            </THead>
            <TBody>
              {rows.map((request) => (
                <TR key={request.id}>
                  <TDPrimary className="whitespace-nowrap font-mono text-xs">
                    {formatDate(request.start_date, "d MMM")}
                    {request.start_date !== request.end_date &&
                      ` to ${formatDate(request.end_date, "d MMM")}`}
                  </TDPrimary>
                  <TD numeric>{request.days}</TD>
                  <TD className="max-w-[30ch] whitespace-pre-wrap break-words">{request.reason}</TD>
                  <TD>
                    <StatusBadge status={request.status} />
                  </TD>
                  <TD>
                    {request.status === "pending" ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        iconLeft="trash"
                        onClick={() =>
                          cancel.mutate(request.id, {
                            onSuccess: () => toast.success("Request cancelled."),
                            onError: (err) =>
                              toast.error(
                                err instanceof ApiError ? err.message : "Could not cancel that.",
                              ),
                          })
                        }
                      >
                        Cancel
                      </Button>
                    ) : (
                      <span className="whitespace-nowrap text-xs text-faint">
                        {request.decided_at
                          ? `${request.decided_by_name ?? "Decided"} · ${formatTimestampDate(
                              request.decided_at,
                              "d MMM",
                            )}`
                          : "—"}
                      </span>
                    )}
                  </TD>
                </TR>
              ))}
            </TBody>
          </TableWrap>
        )}
      </Panel>

      {applying && <WfhDialog onClose={() => setApplying(false)} />}
    </div>
  );
}

/** Working days of `request` that fall inside [start, end]. */
function WfhDialog({ onClose }: { onClose: () => void }) {
  const apply = useApplyWfh();
  const holidays = useHolidays();

  // Calendar date, not work date — the backend validates the filing window
  // against the plain org date, and between midnight and 6am the two differ.
  const today = calendarTodayStr();
  // A day already worked from home can be filed afterwards, back to the 1st of
  // last month; it reaches the approver marked as filed late.
  const earliest = earliestFilingDate(1);
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  // Weekends and fixed holidays inside the range are not days anyone works from
  // home, so the preview matches what the backend will actually record.
  const fixedHolidaySet = useMemo(() => new Set(holidayMap(holidays.data).keys()), [holidays.data]);
  const days =
    startDate && endDate && startDate <= endDate
      ? countWorkingDays(startDate, endDate, fixedHolidaySet)
      : 0;

  async function submit() {
    setError(null);
    if (!startDate || !endDate) {
      setError("Both dates are required.");
      return;
    }
    if (startDate > endDate) {
      setError("The start date cannot be after the end date.");
      return;
    }
    if (startDate < earliest) {
      setError(`Past days can only be filed from the start of last month — pick ${formatDate(earliest)} or later.`);
      return;
    }
    if (!reason.trim()) {
      setError("Give a reason for this request.");
      return;
    }

    const payload: WfhInput = { startDate, endDate, reason: reason.trim() };
    try {
      await apply.mutateAsync(payload);
      toast.success("Work-from-home request submitted.");
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not submit that request.");
    }
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader
          title="Request work from home"
          description="This goes to your manager for a decision. It is a day worked, so it costs no leave. Forgot to ask? You can file for a day this month or last month too."
        />
        <DialogBody className="flex flex-col gap-4">
          {error && (
            <p
              role="alert"
              className="flex items-start gap-2 rounded-lg bg-bad-wash px-3 py-2.5 text-[13px] leading-relaxed text-bad"
            >
              <Icon name="warning-circle" size={16} className="mt-0.5" />
              <span>{error}</span>
            </p>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="From" htmlFor="wfh-start" required>
              <Input
                id="wfh-start"
                type="date"
                min={earliest}
                value={startDate}
                onChange={(event) => {
                  setStartDate(event.target.value);
                  if (event.target.value > endDate) setEndDate(event.target.value);
                }}
              />
            </Field>
            <Field label="To" htmlFor="wfh-end" required>
              <Input
                id="wfh-end"
                type="date"
                min={startDate}
                value={endDate}
                onChange={(event) => setEndDate(event.target.value)}
              />
            </Field>
          </div>

          <Field
            label="Reason"
            htmlFor="wfh-reason"
            required
            hint="Your approver is deciding on this, so say enough for them to decide."
          >
            <Textarea
              id="wfh-reason"
              placeholder="Plumber coming in the morning"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />
          </Field>

          {days > 0 && (
            <p className="flex items-center gap-1.5 text-[13px] text-muted">
              <Icon name="calendar" size={16} className="text-faint" />
              <Badge tone="outline">
                {days} {days === 1 ? "day" : "days"}
              </Badge>
              <span className="text-faint">
                Weekends and holidays in this range aren&rsquo;t counted
              </span>
            </p>
          )}

          <p className="text-xs leading-relaxed text-muted">
            Once it is approved you still check in and check out as usual on those days — the day is
            simply recorded as remote.
          </p>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={apply.isPending}>
            Cancel
          </Button>
          <Button variant="primary" onClick={submit} loading={apply.isPending}>
            Submit request
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
